import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from 'react'

interface Breadcrumb {
  path: string
  label: string
}

interface BreadcrumbContextType {
  setDomainName: (name: string) => void
  setOrgName: (name: string) => void
  /**
   * Callers MUST call this from a plain `useEffect`, not `useLayoutEffect`
   * (123kjkdjzc2) — Layout.tsx clears breadcrumbs on every route change in
   * a `useLayoutEffect`, which is guaranteed to run before any passive
   * `useEffect`. A page setting its own crumbs from `useLayoutEffect` would
   * race that clear instead and could have its crumbs wiped.
   */
  setBreadcrumbs: (breadcrumbs: Breadcrumb[]) => void
  clearBreadcrumbs: () => void
  domainName: string
  orgName: string
  breadcrumbs: Breadcrumb[]
}

const BreadcrumbContext = createContext<BreadcrumbContextType | undefined>(
  undefined
)

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [domainName, setDomainName] = useState('')
  const [orgName, setOrgName] = useState('')
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([])

  // useCallback ensures clearBreadcrumbs has a stable reference so it doesn't
  // invalidate the memoized context value on every render.
  const clearBreadcrumbs = useCallback(() => {
    setBreadcrumbs([])
  }, [])

  const contextValue = useMemo(
    () => ({
      // setState dispatchers from useState are stable by React guarantee;
      // they are safe to include without useCallback.
      setDomainName,
      setOrgName,
      setBreadcrumbs,
      clearBreadcrumbs,
      domainName,
      orgName,
      breadcrumbs,
    }),
    [clearBreadcrumbs, domainName, orgName, breadcrumbs]
  )

  return (
    <BreadcrumbContext.Provider value={contextValue}>
      {children}
    </BreadcrumbContext.Provider>
  )
}

export function useBreadcrumb() {
  const context = useContext(BreadcrumbContext)
  if (!context) {
    throw new Error('useBreadcrumb must be used within a BreadcrumbProvider')
  }
  return context
}
